/**
 * Comprehensive attachment compliance display with accordion UI
 * Shows detailed filtering results for each uploaded file
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Paperclip, ShieldCheck, Download } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { ActionBadge } from './ActionBadge';
import { FileIcon } from './FileIcon';
import { ResourceViolationsList } from './ResourceViolationsList';
import { downloadFile } from '../utils/fileDownload';

interface FilterResult {
  action: 'ALLOW' | 'WARN' | 'AMEND' | 'BLOCK';
  amendment?: string | null;
  internal_monologue?: string;
  reason?: string;
  resource_violations?: Record<string, unknown>;
}

interface AttachmentResult {
  file_id: number | string;
  name?: string;
  filename?: string;
  content_type?: string;
  action: 'ALLOW' | 'WARN' | 'AMEND' | 'BLOCK';
  filter_result?: FilterResult;
  reason?: string;
  amended_file_id?: number | string; // ID of the amended version if it exists
}

interface AttachmentComplianceSectionProps {
  attachmentResults: AttachmentResult[];
  overallAction?: string;
  amendedFileIds?: (string | number)[]; // Array of amended file IDs
}

export const AttachmentComplianceSection: React.FC<AttachmentComplianceSectionProps> = ({
  attachmentResults,
  overallAction,
  amendedFileIds = [],
}) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Build mapping of which files were amended and their corresponding amended file IDs
  // If a file has action=AMEND, we need to find its corresponding amended file ID from the amendedFileIds array
  let amendedFileIndex = 0;
  const amendedFileMap = new Map<string, number | string>();
  attachmentResults.forEach((attachment) => {
    const action = attachment.filter_result?.action || attachment.action;
    if (action === 'AMEND' && amendedFileIndex < amendedFileIds.length) {
      // Map original file ID to the amended file ID
      amendedFileMap.set(String(attachment.file_id), amendedFileIds[amendedFileIndex]);
      amendedFileIndex++;
    }
  });

  const toggleItem = (fileId: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(fileId)) {
      newExpanded.delete(fileId);
    } else {
      newExpanded.add(fileId);
    }
    setExpandedItems(newExpanded);
  };

  const [expandedMonologues, setExpandedMonologues] = useState<Set<string>>(new Set());

  const toggleMonologue = (fileId: string) => {
    const newExpanded = new Set(expandedMonologues);
    if (newExpanded.has(fileId)) {
      newExpanded.delete(fileId);
    } else {
      newExpanded.add(fileId);
    }
    setExpandedMonologues(newExpanded);
  };

  // Count actions for summary
  const actionCounts = attachmentResults.reduce(
    (acc, attachment) => {
      acc[attachment.action] = (acc[attachment.action] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  const getSummaryText = () => {
    const parts: string[] = [];
    if (actionCounts.BLOCK) parts.push(`${actionCounts.BLOCK} blocked`);
    if (actionCounts.AMEND) parts.push(`${actionCounts.AMEND} amended`);
    if (actionCounts.WARN) parts.push(`${actionCounts.WARN} warned`);
    if (actionCounts.ALLOW) parts.push(`${actionCounts.ALLOW} allowed`);
    return parts.length > 0 ? `(${parts.join(', ')})` : '';
  };

  return (
    <div className="w-full space-y-3">
      {/* Section Header with Summary */}
      <div className="border-b border-gray-200 dark:border-gray-700 pb-3">
        <div className="flex items-center gap-2 mb-2">
          <Paperclip size={16} className="text-gray-600 dark:text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Attachments Compliance ({attachmentResults.length} file{attachmentResults.length !== 1 ? 's' : ''})
          </h3>
        </div>
        {overallAction && (
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-600 dark:text-gray-400">Overall Action:</span>
            <ActionBadge action={overallAction as 'ALLOW' | 'WARN' | 'AMEND' | 'BLOCK'} size="sm" />
            <span className="text-gray-500 dark:text-gray-500">{getSummaryText()}</span>
          </div>
        )}
      </div>

      {/* Accordion for each file */}
      <div className="space-y-2">
        {attachmentResults.map((attachment) => {
          const fileKey = String(attachment.file_id);
          const isExpanded = expandedItems.has(fileKey);
          const isMonologueExpanded = expandedMonologues.has(fileKey);

          return (
            <div
              key={fileKey}
              className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800"
            >
              {/* Accordion Header - Always Visible */}
              <div className="w-full px-4 py-3 flex items-center gap-3">
                <button
                  onClick={() => toggleItem(fileKey)}
                  className="flex items-center gap-3 flex-1 hover:opacity-70 transition-opacity"
                >
                  <div className="flex-shrink-0">
                    {isExpanded ? (
                      <ChevronDown size={18} className="text-gray-600 dark:text-gray-400" />
                    ) : (
                      <ChevronRight size={18} className="text-gray-600 dark:text-gray-400" />
                    )}
                  </div>
                  <FileIcon contentType={attachment.content_type || 'application/octet-stream'} size={18} />
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate flex-1 text-left">
                    {attachment.name || attachment.filename || `File ${attachment.file_id}`}
                  </span>
                </button>
                <ActionBadge action={attachment.action} size="sm" />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadFile(attachment.file_id, attachment.name || attachment.filename);
                  }}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                  title="Download file"
                >
                  <Download size={16} className="text-gray-600 dark:text-gray-400" />
                </button>
              </div>

              {/* Accordion Content - Expandable */}
              {isExpanded && (
                <div className="px-4 pb-4 space-y-4 border-t border-gray-100 dark:border-gray-700">
                  {/* File Details Section */}
                  <div className="bg-gray-50 dark:bg-gray-900/50 rounded-md p-3 mt-3">
                    <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
                      📄 File Details
                    </h4>
                    <dl className="text-xs space-y-1">
                      {attachment.content_type && (
                        <div className="flex">
                          <dt className="text-gray-600 dark:text-gray-400 w-24">Type:</dt>
                          <dd className="text-gray-900 dark:text-gray-100 font-mono text-[11px]">
                            {attachment.content_type}
                          </dd>
                        </div>
                      )}
                      <div className="flex">
                        <dt className="text-gray-600 dark:text-gray-400 w-24">File ID:</dt>
                        <dd className="text-gray-900 dark:text-gray-100">{attachment.file_id}</dd>
                      </div>
                    </dl>
                  </div>

                  {/* Compliance Analysis Section */}
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                      <ShieldCheck size={14} />
                      Compliance Analysis
                    </h4>

                    <div className="space-y-3">
                      {/* Action */}
                      <div className="grid grid-cols-[140px_1fr] gap-3 items-start">
                        <span className="text-xs text-gray-600 dark:text-gray-400 pt-1">Action:</span>
                        <ActionBadge action={attachment.filter_result?.action || attachment.action} size="sm" />
                      </div>

                      {/* Reason */}
                      {(attachment.filter_result?.reason || attachment.reason) && (
                        <div className="grid grid-cols-[140px_1fr] gap-3 items-start">
                          <span className="text-xs text-gray-600 dark:text-gray-400 pt-1">Reason:</span>
                          <div className="prose prose-sm dark:prose-invert max-w-md max-h-[200px] overflow-y-auto border border-gray-200 dark:border-gray-700 rounded p-2 bg-white dark:bg-gray-800 text-xs">
                            <ReactMarkdown>{attachment.filter_result?.reason || attachment.reason}</ReactMarkdown>
                          </div>
                        </div>
                      )}

                      {/* Internal Monologue - Collapsible */}
                      {attachment.filter_result?.internal_monologue && (
                        <div className="grid grid-cols-[140px_1fr] gap-3 items-start">
                          <span className="text-xs text-gray-600 dark:text-gray-400 pt-1">Internal Monologue:</span>
                          <div>
                            <button
                              onClick={() => toggleMonologue(fileKey)}
                              className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium flex items-center gap-1"
                            >
                              {isMonologueExpanded ? (
                                <>
                                  <ChevronDown size={14} />
                                  Hide reasoning
                                </>
                              ) : (
                                <>
                                  <ChevronRight size={14} />
                                  Show reasoning
                                </>
                              )}
                            </button>
                            {isMonologueExpanded && (
                              <div className="mt-2 prose prose-sm dark:prose-invert max-w-md max-h-[200px] overflow-y-auto border border-gray-200 dark:border-gray-700 rounded p-2 bg-gray-50 dark:bg-gray-900/50 text-xs">
                                <ReactMarkdown>{attachment.filter_result?.internal_monologue}</ReactMarkdown>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Amendment */}
                      {attachment.filter_result?.amendment && (
                        <div className="grid grid-cols-[140px_1fr] gap-3 items-start">
                          <span className="text-xs text-gray-600 dark:text-gray-400 pt-1">Amendment:</span>
                          <div className="space-y-2">
                            <div className="prose prose-sm dark:prose-invert max-w-md max-h-[200px] overflow-y-auto border border-orange-200 dark:border-orange-700 rounded p-2 bg-orange-50 dark:bg-orange-900/20 text-xs">
                              <ReactMarkdown>{attachment.filter_result?.amendment}</ReactMarkdown>
                            </div>
                            {/* Download amended file button - Always show if there's an amendment */}
                            <button
                              onClick={() => {
                                // Get the AMENDED file ID (the NEW file created by the API)
                                const amendedFileId = amendedFileMap.get(String(attachment.file_id));
                                console.log('📥 Downloading amended file:', {
                                  original_file_id: attachment.file_id,
                                  amended_file_id: amendedFileId,
                                  amendedFileMap: Object.fromEntries(amendedFileMap),
                                  amendedFileIds,
                                });

                                if (!amendedFileId) {
                                  alert('Amended file ID not found. Please try again.');
                                  return;
                                }

                                const baseName = attachment.name || attachment.filename || `file_${attachment.file_id}`;
                                const amendedFileName = `amended_${baseName}`.replace(/\.pdf$/i, '.txt');
                                downloadFile(amendedFileId, amendedFileName);
                              }}
                              className="flex items-center gap-2 px-3 py-2 text-xs bg-orange-100 hover:bg-orange-200 dark:bg-orange-900/40 dark:hover:bg-orange-900/60 text-orange-800 dark:text-orange-200 rounded transition-colors font-medium"
                            >
                              <Download size={14} />
                              Download Amended File (.txt)
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Resource Violations */}
                      {attachment.filter_result?.resource_violations &&
                        Object.keys(attachment.filter_result?.resource_violations).length > 0 && (
                          <div className="grid grid-cols-[140px_1fr] gap-3 items-start">
                            <span className="text-xs text-gray-600 dark:text-gray-400 pt-1">Violations:</span>
                            <div>
                              <ResourceViolationsList violations={attachment.filter_result?.resource_violations} />
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
